/**
 * Add Competitor Modal
 * Modal for adding competitor pricing data
 */

import React, { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { saveCompetitorData, SERVICE_CATEGORIES } from '../services/pricingIntelligenceService';
import './Modal.css';

const AddCompetitorModal = ({ onClose, onSuccess }) => {
  const user = useAuthStore((state) => state.user);

  const [competitorName, setCompetitorName] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  const [rate, setRate] = useState('');
  const [rateType, setRateType] = useState('hourly');
  const [country, setCountry] = useState('Philippines');
  const [source, setSource] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [qualityRating, setQualityRating] = useState(3);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!competitorName || !serviceCategory || !rate) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const competitorData = {
        competitorName,
        serviceCategory,
        rate: parseFloat(rate),
        rateType,
        country,
        source,
        sourceUrl,
        qualityRating,
        notes,
      };

      await saveCompetitorData(user.uid, competitorData);

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Competitor Pricing</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="competitorName">Competitor Name *</label>
              <input
                id="competitorName"
                type="text"
                className="input"
                placeholder="e.g., John's Web Design, Acme Agency"
                value={competitorName}
                onChange={(e) => setCompetitorName(e.target.value)}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="serviceCategory">Service Category *</label>
                <select
                  id="serviceCategory"
                  className="input"
                  value={serviceCategory}
                  onChange={(e) => setServiceCategory(e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {SERVICE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="rateType">Rate Type</label>
                <select
                  id="rateType"
                  className="input"
                  value={rateType}
                  onChange={(e) => setRateType(e.target.value)}
                >
                  <option value="hourly">Hourly</option>
                  <option value="project">Per Project</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="rate">Rate *</label>
                <input
                  id="rate"
                  type="number"
                  className="input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <select
                  id="country"
                  className="input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="Philippines">Philippines</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Singapore">Singapore</option>
                  <option value="India">India</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="qualityRating">Quality Rating</label>
              <div className="quality-rating-input">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    type="button"
                    className={`star-btn ${rating <= qualityRating ? 'active' : ''}`}
                    onClick={() => setQualityRating(rating)}
                  >
                    {rating <= qualityRating ? '★' : '☆'}
                  </button>
                ))}
                <span className="rating-label">
                  {qualityRating === 1 && 'Poor'}
                  {qualityRating === 2 && 'Fair'}
                  {qualityRating === 3 && 'Good'}
                  {qualityRating === 4 && 'Very Good'}
                  {qualityRating === 5 && 'Excellent'}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="source">Source</label>
              <input
                id="source"
                type="text"
                className="input"
                placeholder="e.g., Website, Upwork, Referral"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="sourceUrl">Source URL</label>
              <input
                id="sourceUrl"
                type="url"
                className="input"
                placeholder="https://"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                className="input"
                rows="3"
                placeholder="Any additional information about this competitor's pricing..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Add Competitor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompetitorModal;
